import '@atoms/PickerCombobox/PickerCombobox.scss';
import { CSSProperties, FC, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { MdCheck, MdOutlineOpenInNew, MdSearch } from 'react-icons/md';
import Button from '@srm/ui/button';
import { highlightMatches } from '@srm/ui/lib/highlightMatches';
import { useOptionalUsername } from '@hooks/useOptionalUsername';
import { DictionariesService } from '@services/DictionariesService';
import { IDictionaryData } from '@models/dictionary/IDictionaryData';
import { IDictSettings } from '../IDictSettings';
import { IDictRowView } from '../dictRowView';
import { usePickerNavigation } from '@atoms/PickerCombobox/usePickerNavigation';
import PickerDependenciesHint from '@atoms/PickerCombobox/PickerDependenciesHint';
import PickerSkeleton from '@atoms/PickerCombobox/PickerSkeleton';
import { pickerFooterText } from '@atoms/PickerCombobox/pickerFooterText';
import { useDictComboboxItems } from './useDictComboboxItems';
import { readDictRecentCodes, rememberDictRecentCodes } from './dictRecentCodes';

/** Короткий список выбирается без поиска и подвала — как обычный select. */
const SMALL_LIST = 10;
/** Колонка кода не шире стольких знаков: длиннее — с многоточием, полностью — в подсказке. */
const CODE_MAX_CHARS = 16;

export interface IDictpickerComboboxProps extends IDictSettings {
    onSubmit: (data: IDictionaryData[]) => Promise<void>;
    onClose: () => void;
    /** «Полный список»: модалка справочника с колонками и фильтрами. */
    onOpenFullList?: () => void;
    /** Символ, напечатанный на закрытом поле, — начало поиска. */
    initialQuery?: string;
    /** Подписи пустых полей формы, от которых зависит список (`formValues`). */
    getEmptyDependencies?: () => string[];
}

interface IOptionItem {
    row: IDictionaryData;
    view: IDictRowView;
}

/**
 * Выбор из справочника в выпадающем списке (`isSelectMode`): поиск в шапке, строка «код · название · вторая
 * строка» по разметке `dropdown` колонок (без неё — «Наименование» или код строки), недавние, клавиатура и выход
 * в полный список. Короткий список (до {@link SMALL_LIST}) — без поиска и подвала.
 *
 * Имя пункта для вспомогательных технологий — название; код и вторая строка — его описание.
 */
const DictpickerCombobox: FC<IDictpickerComboboxProps> = (p) => {
    const username = useOptionalUsername();
    const [recentCodes] = useState(() => (p.isFormData ? [] : readDictRecentCodes(username, p.dictName)));
    const [query, setQuery] = useState(p.initialQuery ?? '');
    const items = useDictComboboxItems({ ...p, recentCodes }, query);
    const [selectedCodes, setSelectedCodes] = useState<string[]>(p.selected ?? []);
    const knownRows = useRef(new Map<string, IDictionaryData>());
    const submitting = useRef(false);
    // клики множественного выбора применяются по очереди: следующий видит результат предыдущего
    const multiQueue = useRef<Promise<void>>(Promise.resolve());
    const selectedRef = useRef(selectedCodes);
    selectedRef.current = selectedCodes;
    const listId = useId();

    useEffect(() => setSelectedCodes(p.selected ?? []), [p.selected]);

    const trimmed = query.trim();
    const ready = items.status === 'ready';
    const small = ready && !trimmed && items.total <= SMALL_LIST;
    // показанная строка поиска остаётся до закрытия: иначе, пока грузится новая выдача, она пропала бы с фокусом
    const searchShown = useRef(false);
    const showSearch = searchShown.current || !!p.initialQuery || (ready ? !small : !!trimmed);
    searchShown.current = showSearch;
    const showRecent = !trimmed && !small && items.recent.length > 0;

    const options = useMemo<IOptionItem[]>(() => {
        const layout = items.layout;
        if (!layout) return [];
        const toItem = (row: IDictionaryData) => ({ row, view: layout.view(row) });
        if (!showRecent) return items.rows.map(toItem);
        // недавние не повторяются в общем списке: два пункта с одним именем путают и чтение, и выбор
        const recent = new Set(items.recent.map((row) => row.code));
        return [...items.recent, ...items.rows.filter((row) => !recent.has(row.code))].map(toItem);
    }, [items.layout, items.recent, items.rows, showRecent]);

    // одна ширина кода на весь список (шрифт моноширинный): названия начинаются с одной линии
    const codeChars = useMemo(
        () => Math.min(CODE_MAX_CHARS, Math.max(0, ...options.map((o) => o.view.code?.length ?? 0))),
        [options],
    );

    const { active, setActive, searchRef, listRef, onKeyDown } = usePickerNavigation({
        count: options.length,
        resetKey: trimmed,
        pending: items.pending,
        onChoose: (index) => void choose(options[index]),
        onOpenMore: p.onOpenFullList,
        onClose: p.onClose,
    });

    // Фокус: в строку поиска, а у короткого списка без поиска — в сам список.
    useLayoutEffect(() => {
        if (showSearch) {
            searchRef.current?.focus({ preventScroll: true });
        } else if (ready) {
            listRef.current?.focus({ preventScroll: true });
        }
    }, [showSearch, ready, searchRef, listRef]);

    /** Строки для отправки множественного выбора: неизвестные по коду дочитываются. */
    const resolveRows = async (codes: string[]) => {
        [...items.rows, ...items.recent].forEach((row) => knownRows.current.set(row.code, row));
        const missing = codes.filter((code) => !knownRows.current.has(code));
        if (missing.length) {
            // у псевдосправочника нет выборки по коду в обход отбора: дочитываем той же порцией списка
            const rows = p.isFormData
                ? await items.fetchByCodes(missing)
                : ((await DictionariesService.getItemsByCode(p.dictName, missing)).data ?? []);
            rows.forEach((row) => knownRows.current.set(row.code, row));
        }
        return codes.map((code) => knownRows.current.get(code) ?? ({ code, fields: [] } as unknown as IDictionaryData));
    };

    const toggleMultiple = (row: IDictionaryData) => {
        multiQueue.current = multiQueue.current.then(async () => {
            const before = selectedRef.current;
            const adding = !before.includes(row.code);
            const next = adding ? [...before, row.code] : before.filter((code) => code !== row.code);
            setSelectedCodes(next);
            selectedRef.current = next;
            try {
                await p.onSubmit(await resolveRows(next));
                if (adding && !p.isFormData) rememberDictRecentCodes(username, p.dictName, [row.code]);
            } catch (error) {
                // значение формы не изменилось — отметки возвращаются к нему
                setSelectedCodes(before);
                selectedRef.current = before;
                console.error(error);
            }
        });
    };

    const choose = async ({ row }: IOptionItem) => {
        if (p.isMultiple) {
            toggleMultiple(row);
            return;
        }
        if (submitting.current) return;
        submitting.current = true;
        try {
            if (!p.isFormData) rememberDictRecentCodes(username, p.dictName, [row.code]);
            await p.onSubmit([row]);
            p.onClose();
        } finally {
            submitting.current = false;
        }
    };

    const optionId = (index: number) => `${listId}-option-${index}`;

    const renderOption = (item: IOptionItem, index: number, section: 'recent' | 'all') => {
        const { row, view } = item;
        const selected = selectedCodes.includes(row.code);
        const describedBy = [view.code && `${optionId(index)}-code`, view.subtitle && `${optionId(index)}-sub`]
            .filter(Boolean)
            .join(' ');
        return (
            <div
                key={`${section}-${row.code}`}
                id={optionId(index)}
                data-index={index}
                role="option"
                tabIndex={-1}
                aria-selected={selected}
                aria-label={view.title}
                aria-describedby={describedBy || undefined}
                className={clsx('rf-picker-combobox__option', {
                    'rf-picker-combobox__option--active': index === active,
                    'rf-picker-combobox__option--selected': selected,
                })}
                onMouseMove={() => index !== active && setActive(index)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void choose(item)}
            >
                {p.isMultiple && (
                    <span className="rf-picker-combobox__checkbox" aria-hidden="true">
                        {selected && <MdCheck />}
                    </span>
                )}
                <span className="rf-picker-combobox__main">
                    {view.code ? (
                        <span
                            id={`${optionId(index)}-code`}
                            className="rf-picker-combobox__code"
                            title={view.code.length > CODE_MAX_CHARS ? view.code : undefined}
                        >
                            {highlightMatches(view.code, trimmed, 'rf-picker-combobox__mark')}
                        </span>
                    ) : (
                        codeChars > 0 && <span className="rf-picker-combobox__code" aria-hidden="true" />
                    )}
                    <span className="rf-picker-combobox__text">
                        <span className="rf-picker-combobox__title">
                            {highlightMatches(view.title, trimmed, 'rf-picker-combobox__mark')}
                        </span>
                        {view.subtitle && (
                            <span id={`${optionId(index)}-sub`} className="rf-picker-combobox__subtitle">
                                {highlightMatches(view.subtitle, trimmed, 'rf-picker-combobox__mark')}
                            </span>
                        )}
                    </span>
                </span>
                {!p.isMultiple && selected && <MdCheck className="rf-picker-combobox__tick" aria-hidden="true" />}
            </div>
        );
    };

    const renderEmpty = () => {
        if (items.status === 'error') {
            return (
                <div className="rf-picker-combobox__empty" role="status">
                    <b>Не удалось загрузить справочник</b>
                    <Button size="sm" variant="light" onClick={items.retry}>
                        Повторить
                    </Button>
                </div>
            );
        }
        if (trimmed) {
            return (
                <div className="rf-picker-combobox__empty" role="status">
                    <b>Ничего не найдено по «{trimmed}»</b>
                    Поиск идёт по коду и колонкам, видным в списке.
                </div>
            );
        }
        const dependencies = p.getEmptyDependencies?.() ?? [];
        if (dependencies.length) {
            return (
                <div className="rf-picker-combobox__empty" role="status">
                    <PickerDependenciesHint labels={dependencies} />
                </div>
            );
        }
        return (
            <div className="rf-picker-combobox__empty" role="status">
                <b>{items.filtered ? 'Нет подходящих значений' : 'В справочнике нет значений'}</b>
                {items.filtered && 'Список ограничен условиями формы.'}
            </div>
        );
    };

    const renderList = () => {
        // ошибка новой выдачи — даже при строках прошлой: по ним уже нельзя выбрать
        if (items.status === 'error') return renderEmpty();
        if (!ready && !options.length) return <PickerSkeleton />;
        if (!options.length) return renderEmpty();
        const recentCount = showRecent ? items.recent.length : 0;
        return (
            <>
                {recentCount > 0 && (
                    <div className="rf-picker-combobox__section" role="presentation">
                        Недавние
                    </div>
                )}
                {options.slice(0, recentCount).map((item, i) => renderOption(item, i, 'recent'))}
                {recentCount > 0 && (
                    <div className="rf-picker-combobox__section" role="presentation">
                        Все значения
                    </div>
                )}
                {options.slice(recentCount).map((item, i) => renderOption(item, recentCount + i, 'all'))}
            </>
        );
    };

    const footerText = () =>
        pickerFooterText({
            ready,
            isMultiple: p.isMultiple,
            count: options.length,
            selected: selectedCodes.length,
            shown: items.rows.length,
            total: items.total,
            query: trimmed,
            forms: ['значение', 'значения', 'значений'],
        });

    const showFooter = p.isMultiple || (ready && !small) || !!trimmed;

    return (
        <div className="rf-picker-combobox" onKeyDown={onKeyDown}>
            {showSearch && (
                <div className="rf-picker-combobox__search">
                    <MdSearch className="rf-picker-combobox__search-icon" aria-hidden="true" />
                    <input
                        ref={searchRef}
                        className="rf-picker-combobox__search-input"
                        type="text"
                        role="combobox"
                        aria-label="Поиск по справочнику"
                        aria-controls={listId}
                        aria-expanded="true"
                        aria-autocomplete="list"
                        aria-activedescendant={options[active] ? optionId(active) : undefined}
                        placeholder="Поиск по названию или коду"
                        autoComplete="off"
                        spellCheck={false}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                    />
                </div>
            )}
            <div
                ref={listRef}
                id={listId}
                data-testid="rf-select-list-scroll"
                role="listbox"
                aria-label={p.modalTitle || p.dictName}
                aria-multiselectable={p.isMultiple || undefined}
                aria-busy={!ready || items.pending || undefined}
                aria-activedescendant={!showSearch && options[active] ? optionId(active) : undefined}
                tabIndex={showSearch ? -1 : 0}
                className={clsx(
                    'rf-picker-combobox__list',
                    !ready && options.length > 0 && 'rf-picker-combobox__list--pending',
                )}
                style={codeChars ? ({ '--rf-picker-code-chars': codeChars } as CSSProperties) : undefined}
            >
                {renderList()}
            </div>
            {showFooter && (
                <div className="rf-picker-combobox__footer">
                    <span className="rf-picker-combobox__footer-text" aria-live="polite">
                        {footerText()}
                    </span>
                    {p.isMultiple && (
                        <Button size="sm" onClick={p.onClose}>
                            Готово
                        </Button>
                    )}
                    {p.onOpenFullList && (
                        <Button
                            size="sm"
                            variant="text"
                            onClick={p.onOpenFullList}
                            endAdornment={<MdOutlineOpenInNew size={16} />}
                            title="Полный список (Ctrl+Enter)"
                        >
                            Полный список
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default DictpickerCombobox;
